import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from './logger.service';

export interface CelebrusData {
    BILLING_ACCOUNT_NUMBER: string | null;
    POLICY_NUMBER: string | null;
    POLICY_ROLE: string | null;
    PRODUCT_TYPE: string | null;
    ECN: string | null;
}

@Injectable({
    providedIn: 'root'
})
export class CelebrusTrackingService {
    private celebrusData$ = new BehaviorSubject<CelebrusData>(this.getStoredData());

    constructor(private logger: LoggerService) {
        this.celebrusData$.subscribe((data) => this.syncToStorage(data));
    }

    clearCelebrusSessionStorage(): void {
        this.celebrusData$.next({
            BILLING_ACCOUNT_NUMBER: null,
            POLICY_NUMBER: null,
            POLICY_ROLE: null,
            PRODUCT_TYPE: null,
            ECN: null
        });
    }

    initialize(): void {
        this.logger.debug('Celebrus Tracking Service Initialized');
        this.storePolicyNumber();
        this.storePolicyRole();
        this.storeProductType();
        this.storeBillingAccountNumber();
    }

    storeBillingAccountNumber(billingAccountNumber?: string): void {
        const value = billingAccountNumber || sessionStorage.getItem('billingAccountNumber');
        if (value) this.updateTrackingData({ BILLING_ACCOUNT_NUMBER: value });
    }

    storeECN(ecn: string): void {
        if (ecn) this.updateTrackingData({ ECN: ecn });
    }

    storePolicyNumber(policyNumber?: string): void {
        const value = policyNumber ||
            sessionStorage.getItem('claimsSelectedPolicyNumber') ||
            sessionStorage.getItem('quickIdCardPolicyNumber');

        if (value) this.updateTrackingData({ POLICY_NUMBER: value });
    }

    storePolicyRole(policyRole?: string): void {
        const value = policyRole || sessionStorage.getItem('POLICY_ROLE');
        if (value) {
            this.updateTrackingData({ POLICY_ROLE: value });
        } else {
            sessionStorage.removeItem('POLICY_ROLE');
        }
    }

    storeProductType(): void {
        const value = sessionStorage.getItem('claimsPolicyType');
        if (value) {
            this.updateTrackingData({ PRODUCT_TYPE: value });
        }
    }

    updateTrackingData(patch: Partial<CelebrusData>): void {
        this.celebrusData$.next({
            ...this.celebrusData$.value,
            ...patch
        });
    }

    private getStoredData(): CelebrusData {
        return {
            BILLING_ACCOUNT_NUMBER: sessionStorage.getItem('BILLING_ACCOUNT_NUMBER'),
            POLICY_NUMBER: sessionStorage.getItem('POLICY_NUMBER'),
            POLICY_ROLE: sessionStorage.getItem('POLICY_ROLE'),
            PRODUCT_TYPE: sessionStorage.getItem('PRODUCT_TYPE'),
            ECN: sessionStorage.getItem('ECN')
        };
    }

    private syncToStorage(data: CelebrusData): void {
        for (const [key, value] of Object.entries(data)) {
            if (value) {
                sessionStorage.setItem(key, value);
            } else {
                sessionStorage.removeItem(key);
            }
        }
    }
}
